import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import SVGViewer from '../components/SVGViewer';
import { apiPath, appUrl, assetPath } from '../utils/appPaths';
import { normalizeWiringGuideSvgBackground } from '../utils/wiringGuideSvg';

const BRAND_LOGO_WIDTH = 480;
const BRAND_LOGO_HEIGHT = 200;

const EmbedContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

const EmbedContent = styled.div`
  width: min(1180px, calc(100% - 24px));
  margin: 0 auto;
  padding: 16px 0 20px;
  display: grid;
  gap: 16px;
`;

const GuideHeader = styled.div`
  display: grid;
  gap: 12px;
`;

const GuideHeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const GuideHeaderCopy = styled.div`
  display: grid;
  gap: 6px;
  min-width: 0;
`;

const GuideLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
`;

const GuideTitle = styled.h1`
  margin: 0;
  color: #0f172a;
  font-size: clamp(1.4rem, 2vw, 2rem);
  font-weight: 700;
  line-height: 1.2;
`;

const GuideMeta = styled.div`
  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
`;

const BackLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  color: #3b82f6;
  text-decoration: none;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    background: #ffffff;
    border-color: #3b82f6;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.16);
  }
`;

const BackLinkLogo = styled.img.attrs({
  width: BRAND_LOGO_WIDTH,
  height: BRAND_LOGO_HEIGHT,
  decoding: 'async',
})`
  height: 16px;
  width: auto;
`;

const DiagramPanel = styled.section`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
`;

const ViewerRegion = styled.div`
  height: clamp(360px, 62vh, 760px);

  @media (max-width: 768px) {
    height: clamp(300px, 54vh, 560px);
  }
`;

const EmbeddedGuideViewer = styled(SVGViewer)`
  height: 100%;
  border: none;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
`;

const PinConnectionsSection = styled.div`
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
`;

const PinConnectionsTitle = styled.div`
  padding: 16px 20px;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
`;

const PinTableScroll = styled.div`
  overflow: auto;
  max-height: 320px;
`;

const PinTable = styled.table`
  width: 100%;
  min-width: 520px;
  border-collapse: collapse;
  font-size: 13px;
`;

const PinTableHeader = styled.thead`
  background: #f8fafc;
`;

const PinTableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #334155;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  background: #f8fafc;
  z-index: 1;
`;

const PinTableBody = styled.tbody``;

const PinTableRow = styled.tr`
  &:nth-child(even) {
    background: #fcfdff;
  }

  &:hover {
    background: #f8fbff;
  }
`;

const PinTableCell = styled.td`
  padding: 12px 16px;
  color: #334155;
  border-bottom: 1px solid #e5e7eb;

  &:first-child,
  &:last-child {
    color: #0f172a;
    font-weight: 600;
  }
`;

const PinConnection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
`;

const EmptyTableState = styled.div`
  padding: 18px 20px;
  color: #64748b;
  font-size: 13px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  color: #ef4444;
  font-size: 14px;
  padding: 24px;
  text-align: center;
`;

const EmbedWiringGuide = () => {
  const { slug } = useParams();
  const [wiringGuide, setWiringGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWiringGuide = async () => {
      try {
        setLoading(true);
        const response = await fetch(apiPath(`/wiring-guide/${slug}`));

        if (!response.ok) {
          throw new Error('Failed to load wiring');
        }

        const data = await response.json();
        setWiringGuide(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching wiring:', err);
        setError('Failed to load wiring');
      } finally {
        setLoading(false);
      }
    };

    fetchWiringGuide();
  }, [slug]);

  const connectionCount = useMemo(() => {
    return Array.isArray(wiringGuide?.connections) ? wiringGuide.connections.length : 0;
  }, [wiringGuide?.connections]);
  const viewerSvgContent = useMemo(
    () => normalizeWiringGuideSvgBackground(wiringGuide?.svg_content || ''),
    [wiringGuide?.svg_content]
  );

  if (loading) {
    return (
      <EmbedContainer>
        <LoadingSpinner>Loading wiring...</LoadingSpinner>
      </EmbedContainer>
    );
  }

  if (error || !wiringGuide) {
    return (
      <EmbedContainer>
        <ErrorMessage>{error || 'Wiring not found'}</ErrorMessage>
      </EmbedContainer>
    );
  }

  return (
    <EmbedContainer>
      <EmbedContent>
        <GuideHeader>
          <GuideHeaderTop>
            <GuideHeaderCopy>
              <GuideLabel>Wiring</GuideLabel>
              <GuideTitle>
                {wiringGuide.sensor_name} to {wiringGuide.microcontroller_name}
              </GuideTitle>
              <GuideMeta>
                {connectionCount > 0 ? `${connectionCount} connections` : 'Interactive diagram'}
              </GuideMeta>
            </GuideHeaderCopy>

            <BackLink
              href={appUrl(`/wiring-guide/${slug}`)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ArrowLeft size={14} />
              View on
              <BackLinkLogo src={assetPath('/logo.png')} alt="MicroPinouts" />
            </BackLink>
          </GuideHeaderTop>
        </GuideHeader>

        <DiagramPanel>
          <ViewerRegion>
            <EmbeddedGuideViewer
              svgContent={viewerSvgContent}
              initialZoom={1}
              minZoom={0.2}
              maxZoom={3}
              enableZoom={true}
              enablePan={true}
              enableWheelZoom={false}
              showWheelZoomToggle={true}
            />
          </ViewerRegion>

          <PinConnectionsSection>
            <PinConnectionsTitle>Pin Connections</PinConnectionsTitle>
            {connectionCount > 0 ? (
              <PinTableScroll>
                <PinTable>
                  <PinTableHeader>
                    <tr>
                      <PinTableHeaderCell>Sensor Board Pin</PinTableHeaderCell>
                      <PinTableHeaderCell>Connection</PinTableHeaderCell>
                      <PinTableHeaderCell>Microcontroller Pin</PinTableHeaderCell>
                    </tr>
                  </PinTableHeader>
                  <PinTableBody>
                    {wiringGuide.connections.map((connection, index) => (
                      <PinTableRow key={`${connection.sensorPin || 'sensor'}-${connection.microcontrollerPin || 'micro'}-${index}`}>
                        <PinTableCell>{connection.sensorPin}</PinTableCell>
                        <PinTableCell>
                          <PinConnection>-></PinConnection>
                        </PinTableCell>
                        <PinTableCell>{connection.microcontrollerPin}</PinTableCell>
                      </PinTableRow>
                    ))}
                  </PinTableBody>
                </PinTable>
              </PinTableScroll>
            ) : (
              <EmptyTableState>No pin connections available for this guide yet.</EmptyTableState>
            )}
          </PinConnectionsSection>
        </DiagramPanel>
      </EmbedContent>
    </EmbedContainer>
  );
};

export default EmbedWiringGuide;
